import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';

import { FWorkbenchShell } from '../workbench';
import {
  applyPlanningRecommendation,
  confirmAllPlanningScopes,
  fetchPlanningControl,
  generatePlanningJourney,
  generatePlanningRecommendations,
  openPlanningCycle,
} from './planningcontrol.apis';
import {
  IPlanningControlData,
  IPlanningRecommendation,
  JsonObject,
} from './planningcontrol.models';
import { IPlanningControlCenterProps } from './PlanningControlCenter.types';
import PlanningOverviewPanel from './PlanningOverviewPanel';
import PlanningRecommendationsPanel from './PlanningRecommendationsPanel';
import PlanningPoliciesPanel from './PlanningPoliciesPanel';
import PlanningMatrixScopePanel from './PlanningMatrixScopePanel';
import PlanningJourneyPanel from './PlanningJourneyPanel';
import PlanningReadinessActionPanel from './PlanningReadinessActionPanel';

export interface PlanningSetupWorkbenchProps
  extends IPlanningControlCenterProps {
  planningCycleId: number;
  onBack: () => void;
}

type State = {
  tab: number;
  loading: boolean;
  busy: boolean;
  error: string;
  success: string;
  data: IPlanningControlData | null;
  modifyItem: IPlanningRecommendation | null;
  modifyJson: string;
};

export default class PlanningSetupWorkbench extends React.Component<
  PlanningSetupWorkbenchProps,
  State
> {
  state: State = {
    tab: 0,
    loading: false,
    busy: false,
    error: '',
    success: '',
    data: null,
    modifyItem: null,
    modifyJson: '',
  };

  componentDidMount(): void {
    void this.load();
  }

  componentDidUpdate(previousProps: PlanningSetupWorkbenchProps): void {
    if (previousProps.planningCycleId !== this.props.planningCycleId) {
      this.setState(
        {
          tab: 0,
          data: null,
          error: '',
          success: '',
          modifyItem: null,
          modifyJson: '',
        },
        () => void this.load(),
      );
    }
  }

  public refresh = (): void => {
    void this.load();
  };

  private notify = (
    message: string,
    severity: 'success' | 'info' | 'warning' | 'error' = 'info',
  ): void => {
    this.props.showNotification?.(message, severity);
  };

  private resolveReturnPhase = (): string | null => {
    const raw =
      this.props.params?.returnPhase ??
      this.props.params?.returnphase;

    return typeof raw === 'string' && raw.trim()
      ? raw.trim()
      : null;
  };

  private load = async (): Promise<void> => {
    this.setState({ loading: true, error: '' });

    try {
      const data = await fetchPlanningControl(
        this.props.planningCycleId,
      );

      this.setState({
        data,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unable to load the Planning Setup workspace.',
      });
    }
  };

  private run = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ): Promise<void> => {
    this.setState({ busy: true, error: '', success: '' });

    try {
      await action();
      await this.load();
      this.setState({ success: successMessage });
      this.notify(successMessage, 'success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'The action failed.';

      this.setState({ error: message });
      this.notify(message, 'error');
    } finally {
      this.setState({ busy: false });
    }
  };

  private generateRecommendations = (): Promise<void> =>
    this.run(
      () =>
        generatePlanningRecommendations(
          this.props.planningCycleId,
          true,
        ),
      'Recommendations and Matrix Scope generated.',
    );

  private accept = (item: IPlanningRecommendation): void => {
    void this.run(
      () => applyPlanningRecommendation(item.rid, 'ACCEPTED', null),
      `${item.rct} accepted.`,
    );
  };

  private ignore = (item: IPlanningRecommendation): void => {
    void this.run(
      () => applyPlanningRecommendation(item.rid, 'IGNORED', null),
      `${item.rct} ignored.`,
    );
  };

  private beginModify = (item: IPlanningRecommendation): void => {
    this.setState({
      modifyItem: item,
      modifyJson: JSON.stringify(item.prv || {}, null, 2),
    });
  };

  private submitModify = (): void => {
    const item = this.state.modifyItem;
    if (!item) return;

    let value: JsonObject;

    try {
      value = JSON.parse(this.state.modifyJson) as JsonObject;
    } catch {
      this.setState({
        error: 'Modified recommendation value is not valid JSON.',
      });
      return;
    }

    this.setState({
      modifyItem: null,
      modifyJson: '',
    });

    void this.run(
      () => applyPlanningRecommendation(item.rid, 'MODIFIED', value),
      `${item.rct} modified.`,
    );
  };

  private confirmScope = (): Promise<void> =>
    this.run(
      () => confirmAllPlanningScopes(this.props.planningCycleId),
      'Matrix Scope confirmed.',
    );

  private generateJourney = (): Promise<void> =>
    this.run(
      () => generatePlanningJourney(this.props.planningCycleId, true),
      'Planning Journey generated.',
    );

  private openCycle = (): Promise<void> =>
    this.run(async () => {
      const result = await openPlanningCycle(
        this.props.planningCycleId,
        false,
      );

      // this.props.setPlanningContext?.(
      //   result.context as Parameters<
      //     NonNullable<PlanningSetupWorkbenchProps['setPlanningContext']>
      //   >[0],
      // );

      if (this.props.handleBackToCycles){
        this.props.handleBackToCycles();
      }

      const returnPhase = this.resolveReturnPhase();

      if (this.props.selectPlanningPhase) {
        this.props.selectPlanningPhase(returnPhase);
      } else {
        this.props.setPage?.(
          returnPhase || 'HISTORICAL_DATA', //'/bie/historical-matrix'
        );
      }
    }, 'Planning Cycle opened.');

  private renderContent(): React.ReactNode {
    const { data, error, tab, busy, loading } = this.state;

    if (!data) {
      return (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">
              Planning Setup is unavailable
            </Typography>

            <Typography color="text.secondary">
              {error ||
                (loading
                  ? 'Loading the selected Planning Cycle…'
                  : 'No Planning Setup data was returned.')}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => void this.load()}
              >
                Retry
              </Button>

              <Button
                startIcon={<ArrowBackIcon />}
                onClick={this.props.onBack}
              >
                Planning Cycles
              </Button>
            </Stack>
          </Stack>
        </Paper>
      );
    }

    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={800}>
                {data.overview.pcn}
              </Typography>
              <Typography color="text.secondary">
                {data.overview.pcc}
              </Typography>
            </Box>

            <Typography fontWeight={700}>
              Readiness {Number(data.readiness.score || 0).toFixed(0)}%
            </Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined">
          <Tabs
            value={tab}
            onChange={(_, value: number) =>
              this.setState({ tab: value })
            }
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Recommendations" />
            <Tab label="Policies" />
            <Tab label="Matrix Scope" />
            <Tab label="Journey" />
            <Tab label="Readiness" />
          </Tabs>

          <Divider />

          <Box p={2}>
            {tab === 0 && (
              <PlanningOverviewPanel
                overview={data.overview}
                busy={busy}
                onChangeCycle={this.props.onBack}
              />
            )}

            {tab === 1 && (
              <PlanningRecommendationsPanel
                recommendations={data.recommendations || []}
                busy={busy}
                onGenerate={() => void this.generateRecommendations()}
                onAccept={this.accept}
                onModify={this.beginModify}
                onIgnore={this.ignore}
              />
            )}

            {tab === 2 && (
              <PlanningPoliciesPanel policies={data.policies || []} />
            )}

            {tab === 3 && (
              <PlanningMatrixScopePanel
                scope={data.scope || []}
                busy={busy}
                onConfirmAll={() => void this.confirmScope()}
              />
            )}

            {tab === 4 && (
              <PlanningJourneyPanel
                journey={data.journey || []}
                busy={busy}
                onGenerate={() => void this.generateJourney()}
              />
            )}

            {tab === 5 && (
              <PlanningReadinessActionPanel
                readiness={data.readiness}
                busy={busy}
                onOpenCycle={() => void this.openCycle()}
              />
            )}
          </Box>
        </Paper>
      </Stack>
    );
  }

  render(): React.ReactNode {
    const { loading, busy, error, success, data } = this.state;

    return (
      <FWorkbenchShell
        title="Planning Setup"
        subtitle="Configure defaults, recommendations, policies, Matrix Scope, journey and readiness for the selected Planning Cycle."
        icon={<SettingsSuggestIcon />}
        loading={loading || busy}
        error={error}
        success={success}
        onRefresh={() => void this.load()}
      >
        <Stack spacing={2}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={this.props.onBack}
            >
              Planning Cycles
            </Button>
          </Box>

          {/* The shell and Back action remain mounted even when loading fails. */}
          {error && data ? <Alert severity="error">{error}</Alert> : null}

          {this.renderContent()}
        </Stack>

        <Dialog
          open={Boolean(this.state.modifyItem)}
          onClose={() =>
            this.setState({ modifyItem: null, modifyJson: '' })
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Modify Recommendation</DialogTitle>

          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={8}
              margin="dense"
              label="Applied Value (JSON)"
              value={this.state.modifyJson}
              onChange={(event) =>
                this.setState({ modifyJson: event.target.value })
              }
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                this.setState({ modifyItem: null, modifyJson: '' })
              }
            >
              Cancel
            </Button>

            <Button variant="contained" onClick={this.submitModify}>
              Apply Modification
            </Button>
          </DialogActions>
        </Dialog>
      </FWorkbenchShell>
    );
  }
}
